import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import AdminDeleteConversionButton from "@/components/AdminDeleteConversionButton";
import { api } from "@/lib/api";
import { Link as RouterLink } from "react-router-dom";
import { DollarSign, TrendingUp, Calendar, Clock, History, ArrowRight, CheckCircle2, Wallet, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

function fmtMoney(n, currency = "$") {
  const s = (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency.length > 1 ? `${currency} ${s}` : `${currency}${s}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return "—"; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return "—"; }
}

export default function EarningsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/earnings");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const summary = data?.summary;
  const lastPayout = data?.last_payout;
  const conversions = data?.conversions || [];
  const balance = Number(data?.affiliate_balance || 0);
  const pendingRequest = data?.pending_payout_request;
  const hasPending = summary && summary.total_sales_count > 0;
  const hasAnything = hasPending || balance > 0 || conversions.length > 0 || pendingRequest;

  const requestPayout = async () => {
    setRequesting(true);
    try {
      await api.post("/share/payouts/request");
      toast.success("Payout requested — admin will review and process it");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't request payout");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow text-[#002FA7]">My commissions</div>
          <h1 className="text-4xl sm:text-5xl tracking-tight font-black text-[#111827]">Your earnings</h1>
          <p className="text-sm text-[#4B5563] mt-2 max-w-2xl">
            You earn <span className="font-bold text-[#111827]">10%</span> of every sale your leads generate for Matrix. This page shows your <span className="font-bold">current pending</span> earnings — once admin pays you out, the totals reset to zero and the snapshot moves to <RouterLink to="/dashboard/payouts" className="text-[#002FA7] font-bold hover:underline">Payout history</RouterLink>.
          </p>
        </div>
        <RouterLink
          to="/dashboard/payouts"
          data-testid="earnings-view-history"
          className="btn-secondary inline-flex items-center gap-2"
        >
          <History size={14} /> Payout history
        </RouterLink>
      </div>

      {/* Last payout banner */}
      {lastPayout && (
        <div className="rounded-lg border border-[#BBF7D0] bg-[#F0FDF4] p-4 mb-6 flex items-center justify-between gap-3 flex-wrap" data-testid="earnings-last-payout-banner">
          <div className="flex items-center gap-3">
            <CheckCircle2 size={18} className="text-[#166534] flex-shrink-0" />
            <div className="text-sm">
              <span className="text-[#166534] font-bold">Last paid out: {fmtMoney(lastPayout.affiliate_amount)}</span>
              <span className="text-[#4B5563]"> on {fmtDate(lastPayout.paid_at)}</span>
            </div>
          </div>
          <RouterLink to="/dashboard/payouts" className="text-xs font-bold text-[#166534] hover:underline inline-flex items-center gap-1">
            View receipt <ArrowRight size={12} />
          </RouterLink>
        </div>
      )}

      {/* Share-to-earn balance — driven by external conversion postbacks */}
      {(balance > 0 || pendingRequest || conversions.length > 0) && (
        <div className="card-flat p-6 mb-6" data-testid="share-balance-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center"><Wallet size={20} /></div>
              <div>
                <div className="label-eyebrow text-[#4B5563]">Share-to-earn balance</div>
                <div className="font-display font-black text-3xl text-[#002FA7]" data-testid="share-balance-value">{fmtMoney(balance, "GYD")}</div>
                {pendingRequest && (
                  <div className="text-xs text-[#92400E] bg-[#FEF3C7] rounded px-2 py-1 inline-block mt-1">Payout request pending — {fmtMoney(pendingRequest.amount, "GYD")} requested {fmtDate(pendingRequest.created_at)}</div>
                )}
              </div>
            </div>
            <button
              onClick={requestPayout}
              disabled={requesting || balance <= 0 || !!pendingRequest}
              data-testid="request-payout-button"
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
            >
              {requesting ? "Requesting…" : pendingRequest ? "Request pending" : "Request payout"}
            </button>
          </div>

          {conversions.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[#E5E7EB]">
              <div className="label-eyebrow text-[#4B5563] mb-3 flex items-center gap-2"><ShoppingBag size={12} /> Conversion order history ({conversions.length})</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="conversions-table">
                  <thead>
                    <tr className="text-left text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF] border-b border-[#E5E7EB]">
                      <th className="py-2">Order</th>
                      <th className="py-2 text-right">Order total</th>
                      <th className="py-2 text-right">You earned</th>
                      <th className="py-2 text-right">When</th>
                      <th className="py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {conversions.map((c) => (
                      <tr key={c.conversion_id} className="border-b border-[#F3F4F6]" data-testid={`conversion-row-${c.conversion_id}`}>
                        <td className="py-2.5 font-mono text-xs text-[#111827]">{c.order_id}</td>
                        <td className="py-2.5 text-right font-mono text-[#111827]">{fmtMoney(c.total, c.currency || "GYD")}</td>
                        <td className="py-2.5 text-right font-mono font-bold text-[#166534]">{fmtMoney(c.commission_total || 0, c.currency || "GYD")}</td>
                        <td className="py-2.5 text-right text-xs text-[#4B5563]">{fmtDateTime(c.occurred_at)}</td>
                        <td className="py-2.5 text-right">
                          <AdminDeleteConversionButton
                            conversion={c}
                            onDeleted={() => load()}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card-flat h-28 animate-pulse bg-[#F9FAFB]" />
          ))}
        </div>
      ) : !hasPending && !hasAnything ? (
        <EmptyState hasHistory={!!lastPayout} />
      ) : !hasPending ? null : (
        <>
          {/* Summary cards — current cycle only */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8" data-testid="earnings-summary">
            <SummaryCard
              testid="earnings-pending"
              label="Pending payout"
              value={fmtMoney(summary.pending_commission)}
              icon={Clock}
              accent
            />
            <SummaryCard
              testid="earnings-this-month"
              label="This month"
              value={fmtMoney(summary.this_month_commission)}
              icon={Calendar}
            />
            <SummaryCard
              testid="earnings-sales-count"
              label="Sales this cycle"
              value={summary.total_sales_count}
              icon={TrendingUp}
            />
          </div>

          <div className="card-flat p-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="label-eyebrow text-[#4B5563]">Volume your leads sent to Matrix this cycle</div>
                <div className="font-display font-black text-3xl text-[#111827] mt-1">{fmtMoney(summary.total_sales_amount)}</div>
              </div>
              <div className="text-right">
                <div className="label-eyebrow text-[#4B5563]">Your 10% commission</div>
                <div className="font-display font-black text-3xl text-[#002FA7] mt-1">{fmtMoney(summary.pending_commission)}</div>
              </div>
            </div>
          </div>

          {/* Per-lead breakdown */}
          <div className="card-flat overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E5E7EB]">
              <div className="label-eyebrow text-[#4B5563]">Per-lead breakdown — current cycle</div>
              <div className="font-display font-extrabold text-xl text-[#111827]">{data.leads.length} {data.leads.length === 1 ? "lead" : "leads"} with sales</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="earnings-leads-table">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <tr className="text-left">
                    <th className="px-6 py-3 text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF]">Lead</th>
                    <th className="px-6 py-3 text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF] text-right">Sales</th>
                    <th className="px-6 py-3 text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF] text-right">Sold to Matrix</th>
                    <th className="px-6 py-3 text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF] text-right">Your 10%</th>
                    <th className="px-6 py-3 text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF] text-right">Last sale</th>
                  </tr>
                </thead>
                <tbody>
                  {data.leads.map((l) => (
                    <tr key={l.lead_id || "direct"} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`earnings-lead-row-${l.lead_id || "direct"}`}>
                      <td className="px-6 py-4">
                        <div className="font-bold text-[#111827]">{l.lead_name}</div>
                        {l.lead_email && <div className="text-xs text-[#9CA3AF]">{l.lead_email}</div>}
                      </td>
                      <td className="px-6 py-4 text-right text-[#111827]">{l.sales_count}</td>
                      <td className="px-6 py-4 text-right font-mono text-[#111827]">{fmtMoney(l.total_amount)}</td>
                      <td className="px-6 py-4 text-right font-mono font-bold text-[#002FA7]">{fmtMoney(l.total_commission)}</td>
                      <td className="px-6 py-4 text-right text-xs text-[#4B5563]">{fmtDateTime(l.last_sale_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}

function SummaryCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF]">{label}</div>
        <Icon size={16} className={accent ? "text-[#002FA7]" : "text-[#4B5563]"} />
      </div>
      <div className={`font-display font-black text-3xl mt-2 ${accent ? "text-[#002FA7]" : "text-[#111827]"}`}>{value}</div>
    </div>
  );
}

function EmptyState({ hasHistory }) {
  return (
    <div className="card-flat p-12 text-center" data-testid="earnings-empty">
      <div className="mx-auto h-14 w-14 rounded-full bg-[#F3F4F6] flex items-center justify-center mb-4">
        <DollarSign size={24} className="text-[#4B5563]" />
      </div>
      <div className="font-display font-black text-2xl text-[#111827]">{hasHistory ? "You're all paid up" : "No commissions yet"}</div>
      <p className="text-sm text-[#4B5563] mt-2 max-w-md mx-auto">
        {hasHistory
          ? <>All your earnings to date have been paid out. New sales by your leads will show up here as pending commissions. Check the <RouterLink to="/dashboard/payouts" className="text-[#002FA7] font-bold hover:underline">payout history</RouterLink> for previous payouts.</>
          : <>Once admin records a sale on one of the leads you captured, your <span className="font-bold">10%</span> commission will appear here, broken down per lead with running totals.</>
        }
      </p>
    </div>
  );
}
